import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CellSignInFlow } from "@/components/cells/cell-sign-in-flow";
import { findCell, isRemoteCell } from "@/lib/cells/cells";

const searchSchema = z.object({
  cell: z.string(),
  redirect: z
    .string()
    .regex(/^\/(?![/\\])/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/cell-sign-in")({
  component: CellSignInPage,
  validateSearch: zodValidator(searchSchema),
});

function CellSignInPage() {
  const { cell: cellId, redirect } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cell = findCell(cellId);
  const returnTo = redirect ?? "/tenants";
  const baseURL = cell?.remote?.tenantsBaseUrl;

  const onSuccess = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [{ baseURL }] });
    void navigate({ href: returnTo });
  }, [baseURL, navigate, queryClient, returnTo]);

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-4 py-12">
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void navigate({ href: returnTo });
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-center">
            <h1>Sign in to {cellId}</h1>
          </CardTitle>
          <CardDescription className="text-center">
            Tenants in this cell use a separate sign-in. Your current session
            stays as it is.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {cell && isRemoteCell(cell) ? (
            <CellSignInFlow cell={cell} onSuccess={onSuccess} />
          ) : (
            <p className="text-muted-foreground text-center text-sm">
              No cell named {cellId} is configured.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
